/**
 * authStore — Gerencia autenticação Google OAuth e dados do usuário logado
 *
 * Consumidores: App, DashboardLayout, LandingPage, useAuth, Preferencias, FeedbackModal
 * Dependências: googleAuth, googleDrive
 * Persistência: localStorage (bigwa_user) + Google OAuth tokens
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { User } from '../types/user';
import { googleAuth } from '../services/googleAuth';
import { googleDrive } from '../services/googleDrive';
import { refsCache, researchCache } from './driveProject/projectCache';
import { logger } from '../utils/logger';
import { safeSetItem } from '../utils/safeLocalStorage';

const STORAGE_KEY_USER = 'bigwa_user';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticating: boolean;
  isInitialized: boolean;
  error: string | null;

  initialize: () => Promise<void>;
  setUser: (user: User | null) => void;
  setAuthenticating: (isAuthenticating: boolean) => void;
  login: () => void;
  logout: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    (set, get) => ({
      user: null,
      isLoading: false,
      isAuthenticating: false,
      isInitialized: false,
      error: null,

      /**
       * Initialize auth:
       * 1. Check URL for ?auth=success callback from backend OAuth
       * 2. Initialize googleAuth (restores from localStorage or backend session)
       * 3. Restore user from localStorage if token is valid
       */
      initialize: async () => {
        try {
          // 1. Check if we're returning from OAuth callback
          const params = new URLSearchParams(window.location.search);
          const authStatus = params.get('auth');
          const userParam = params.get('user');
          const errorParam = params.get('error');

          // Clean URL params regardless
          if (authStatus || errorParam) {
            window.history.replaceState({}, '', window.location.pathname);
          }

          if (errorParam) {
            logger.error('AuthStore', 'OAuth error:', errorParam);
            set({ error: `Erro no login: ${errorParam}`, isInitialized: true });
            return;
          }

          if (authStatus === 'success' && userParam) {
            // Returning from successful OAuth — get token from backend
            try {
              const userData = JSON.parse(atob(userParam));

              // Call /api/auth/refresh to get the access token
              const response = await fetch('/api/auth/refresh', {
                method: 'POST',
                credentials: 'include',
              });

              if (response.ok) {
                const data = await response.json();

                // Set token in googleAuth
                googleAuth.setToken(data.accessToken, data.expiresAt);

                const user: User = {
                  id: userData.id,
                  email: userData.email,
                  name: userData.name,
                  photoURL: userData.picture,
                };

                get().setUser(user);

                // Initialize googleAuth (will skip backend call since we have a valid token)
                await googleAuth.initialize();

                set({ isInitialized: true });
                return;
              } else {
                logger.error('AuthStore', 'Failed to get token after callback');
              }
            } catch (e) {
              logger.warn('AuthStore', 'Error parsing callback data:', e);
            }
          }

          // 2. Normal initialization — restore from localStorage/backend session
          await googleAuth.initialize();

          // 3. If token is valid, restore user from localStorage
          if (googleAuth.hasValidToken()) {
            const savedUser = localStorage.getItem(STORAGE_KEY_USER);
            if (savedUser) {
              try {
                const user = JSON.parse(savedUser);
                set({ user, isInitialized: true });
                return;
              } catch (_e) {
                logger.warn('AuthStore', 'Error parsing saved user');
              }
            }

            // Have token but no saved user — fetch user info
            const userInfo = await googleAuth.getUserInfo();
            if (userInfo) {
              const user: User = {
                id: '',
                email: userInfo.email,
                name: userInfo.name,
                photoURL: userInfo.picture,
              };
              get().setUser(user);
              set({ isInitialized: true });
              return;
            }
          }

          // No valid session
          set({ user: null, isInitialized: true });
        } catch (error) {
          logger.error('AuthStore', 'Initialization error:', error);
          set({ user: null, isInitialized: true });
        }
      },

      setUser: (user) => {
        // SECURITY: Detect user change and clear data
        const currentUser = get().user;
        if (user && currentUser && user.email !== currentUser.email) {
          localStorage.removeItem("bigwa_activeProjectId");
          localStorage.removeItem("bigwa-library");
          sessionStorage.clear();
        }
        // Save to localStorage
        if (user) {
          safeSetItem(STORAGE_KEY_USER, JSON.stringify(user));
        } else {
          localStorage.removeItem(STORAGE_KEY_USER);
        }

        const updates: Partial<AuthState> = { user, error: null };
        if (user !== null) {
          updates.isAuthenticating = false;
        }
        set(updates);
      },

      setAuthenticating: (isAuthenticating) => {
        set({ isAuthenticating });
      },

      /**
       * Login — redirect to /api/auth/login (no popup)
       */
      login: () => {
        set({ isAuthenticating: true, error: null });
        googleAuth.login();
        // Page will redirect — no further code runs
      },

      logout: async () => {
        set({ isLoading: true, error: null });
        try {
          googleDrive.reset();

          // Clear IndexedDB caches (prevents stale data on re-login + cross-user protection)
          // dispose() clears L1 memory + closes L2 IndexedDB connection
          refsCache.dispose();
          researchCache.dispose();
          // Must await before redirect so data is actually cleared
          await Promise.all([
            // Delete remaining IndexedDB databases not covered above (cross-user protection)
            new Promise<void>(resolve => { indexedDB.deleteDatabase('bigwa_cache'); resolve(); }).catch(() => {}),
            new Promise<void>(resolve => { indexedDB.deleteDatabase('bigwa-library-works'); resolve(); }).catch(() => {}),
            new Promise<void>(resolve => { indexedDB.deleteDatabase('bigwa-conversations'); resolve(); }).catch(() => {}),
            new Promise<void>(resolve => { indexedDB.deleteDatabase('bigwa-offline-queue'); resolve(); }).catch(() => {}),
          ]);

          // Clear ALL Bigwa data from localStorage (exceto activeProjectId para restaurar ao relogar)
          const keysToPreserve = ['bigwa_activeProjectId', 'bigwa-preferences'];
          const keysToRemove = Object.keys(localStorage).filter(key =>
            (key.startsWith('bigwa') || key.startsWith('BIGWA')) && !keysToPreserve.includes(key)
          );
          keysToRemove.forEach(key => localStorage.removeItem(key));

          sessionStorage.clear();

          set({ user: null, isLoading: false });

          // signOut redirects to /api/auth/logout which clears cookie + Redis
          googleAuth.signOut();
          // Page will redirect — no further code runs
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : String(error);
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      updateProfile: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const currentUser = get().user;
          if (!currentUser) throw new Error('Usuário não autenticado');

          const updatedUser = { ...currentUser, ...data };
          get().setUser(updatedUser);
          set({ isLoading: false });
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : String(error);
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    { name: 'AuthStore' }
  )
);
